import { RankedListWidget } from '../RankedListWidget/ranked-list-widget';
import { useTranslation } from 'react-i18next';
import { toNumberString } from '@client/toolkit';
import { usePostContractsAverageLengthDashboardMutation } from '@client/api';
import { useEffect, useMemo } from 'react';
import { NoDataWidget } from '../NoDataWidget/no-data-widget';

interface ContractLengthWidgetProps {
  buildingIds: string[];
  selectedDateFrom: string;
  selectedDateTo: string;
}

export const ContractLengthWidget = ({ buildingIds, selectedDateFrom, selectedDateTo }: ContractLengthWidgetProps) => {
  const { t } = useTranslation('widget');
  const [trigger, { data, isLoading }] = usePostContractsAverageLengthDashboardMutation();

  useEffect(() => {
    if (!selectedDateFrom || !selectedDateTo) return;
    trigger({
      startDate: selectedDateFrom,
      endDate: selectedDateTo,
      body: {
        buildingIds: buildingIds,
      },
    });
  }, [buildingIds, selectedDateFrom, selectedDateTo, trigger]);

  const topThreeHighest = useMemo(() => {
    return data?.topThreeBuildingsByRemainingLength.map((item) => ({
      id: item.buildingId,
      label: item.buildingName,
      value: item.agreementRemainingLengthYears,
    }));
  }, [data?.topThreeBuildingsByRemainingLength]);
  
  const bottomThreeLowest = useMemo(() => {
    return data?.bottomThreeBuildingsByRemainingLength.map((item) => ({
      id: item.buildingId,
      label: item.buildingName,
      value: item.agreementRemainingLengthYears,
    }));
  }, [data?.bottomThreeBuildingsByRemainingLength]);

  const isNoDataAvailable = useMemo(
    () => data && topThreeHighest?.length === 0 && bottomThreeLowest?.length === 0,
    [data, topThreeHighest, bottomThreeLowest],
  );

  if (isNoDataAvailable)
    return <NoDataWidget title={t('contractLength.title')} noDataText={t('contractLength.noData')} />;

  return (
    <RankedListWidget
      to="/reporting/contract-length"
      title={t('contractLength.title')}
      tooltip={t('contractLength.tooltip')}
      value={toNumberString(data?.averageContractLengthYears ?? 0)}
      unit={t('contractLength.unit')}
      rankUnit={t('contractLength.rankUnit')}
      loading={isLoading || !data}
      highListTitle={t('contractLength.topThreeHighest')}
      lowListTitle={t('contractLength.bottomThreeLowest')}
      highListData={topThreeHighest || []}
      lowListData={bottomThreeLowest || []}
    />
  );
};
